import type { Place } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, MapPin } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from '@/components/feedback/toaster';
import { ConfirmDialog } from '@/features/shared/confirm-dialog';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { placeSubtitle } from '@/features/tasting/format';
import { LazyTastingMap } from '@/features/tasting/map/lazy-map';
import { api } from '@/lib/api';
import { describeError, isVersionConflict, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { VisitCard } from './visits-page';

function usePlaces() {
  return useQuery({
    queryKey: queryKeys.tasting.places({ limit: 100 }),
    queryFn: () => api.places.list({ limit: 100 }),
  });
}

export function TastingPlacesPage() {
  const { t } = useTranslation();
  const places = usePlaces();
  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('tasting.places.title')}
        description={t('tasting.places.description')}
        backTo="/tasting"
      />
      {places.isLoading ? (
        <Spinner label={t('app.loading')} />
      ) : places.error ? (
        <Alert variant="destructive">{describeError(places.error)}</Alert>
      ) : !places.data?.items.length ? (
        <EmptyState
          title={t('tasting.places.emptyTitle')}
          description={t('tasting.places.emptyBody')}
        />
      ) : (
        <ul className="divide-y rounded-lg border">
          {places.data.items.map((place) => (
            <li key={place.id}>
              <Link
                to={`/tasting/places/${place.id}`}
                className="flex min-h-[56px] items-center gap-3 px-3 py-2 hover:bg-accent/40"
              >
                <MapPin className="size-5 shrink-0 text-primary" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{place.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {placeSubtitle(place)}
                  </span>
                </span>
                <Badge variant="muted">
                  {t('tasting.places.visitCount', { count: place.visitCount })}
                </Badge>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </PageContainer>
  );
}

function PlaceEditor({ place, others }: { place: Place; others: Place[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ids = { name: useId(), cuisines: useId() };
  const [name, setName] = useState(place.name);
  const [cuisines, setCuisines] = useState(place.cuisines.join(', '));
  const [mergeSource, setMergeSource] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [reassignTo, setReassignTo] = useState('');

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.tasting.all });
  };
  const save = useMutation({
    mutationFn: () =>
      api.places.update(place.id, {
        version: place.version,
        name: name.trim(),
        cuisines: cuisines
          .split(',')
          .map((c) => c.trim().toLowerCase())
          .filter(Boolean)
          .slice(0, 12),
      }),
    onSuccess: () => {
      toast.success(t('tasting.places.saved'));
      refresh();
    },
    onError: (e) =>
      isVersionConflict(e) ? toast.error(t('tasting.visit.conflict')) : toastError(e),
  });
  const merge = useMutation({
    mutationFn: () => api.places.merge(place.id, mergeSource),
    onSuccess: () => {
      toast.success(t('tasting.places.merged'));
      setMergeSource('');
      refresh();
    },
    onError: (e) => toastError(e),
  });
  const remove = useMutation({
    mutationFn: () => api.places.remove(place.id, reassignTo || undefined),
    onSuccess: () => {
      toast.success(t('tasting.places.deleted'));
      refresh();
      navigate('/tasting/places');
    },
    onError: (e) => toastError(e),
  });
  const needsReassign = place.visitCount > 0;

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <h2 className="font-semibold">{t('tasting.places.manage')}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor={ids.name}>{t('tasting.places.name')}</Label>
            <Input
              id={ids.name}
              className="mt-1"
              value={name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor={ids.cuisines}>{t('tasting.places.cuisines')}</Label>
            <Input
              id={ids.cuisines}
              className="mt-1"
              value={cuisines}
              placeholder={t('tasting.places.cuisinesPlaceholder')}
              onChange={(e) => setCuisines(e.target.value)}
            />
          </div>
        </div>
        <Button
          size="sm"
          disabled={!name.trim()}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          {t('common.save')}
        </Button>

        {others.length > 0 && (
          <div className="border-t pt-4">
            <Label>{t('tasting.places.mergeLabel')}</Label>
            <p className="text-xs text-muted-foreground">{t('tasting.places.mergeHint')}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Select value={mergeSource} onValueChange={setMergeSource}>
                <SelectTrigger className="w-64" aria-label={t('tasting.places.mergeLabel')}>
                  <SelectValue placeholder={t('tasting.places.mergePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {others.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                disabled={!mergeSource}
                loading={merge.isPending}
                onClick={() => merge.mutate()}
              >
                {t('tasting.places.mergeAction')}
              </Button>
            </div>
          </div>
        )}

        <div className="border-t pt-4">
          <Button size="sm" variant="ghost" onClick={() => setDeleting(true)}>
            {t('tasting.places.delete')}
          </Button>
        </div>
      </CardContent>

      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={t('tasting.places.deleteTitle', { name: place.name })}
        description={
          needsReassign ? t('tasting.places.deleteNeedsReassign') : t('tasting.places.deleteBody')
        }
        confirmLabel={t('common.delete')}
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (needsReassign && !reassignTo) return;
          remove.mutate();
        }}
      >
        {needsReassign &&
          (others.length ? (
            <Select value={reassignTo} onValueChange={setReassignTo}>
              <SelectTrigger aria-label={t('tasting.places.reassignLabel')}>
                <SelectValue placeholder={t('tasting.places.reassignLabel')} />
              </SelectTrigger>
              <SelectContent>
                {others.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Alert variant="warning">{t('tasting.places.deleteNoTarget')}</Alert>
          ))}
      </ConfirmDialog>
    </Card>
  );
}

/** All visits for one restaurant, plus rename / merge / delete of the place. */
export function TastingPlaceDetailPage() {
  const { placeId = '' } = useParams();
  const { t } = useTranslation();
  const place = useQuery({
    queryKey: queryKeys.tasting.place(placeId),
    queryFn: () => api.places.get(placeId),
    enabled: Boolean(placeId),
  });
  const all = usePlaces();
  const visits = useQuery({
    queryKey: queryKeys.tasting.visits({ placeId, scope: 'mine' }),
    queryFn: () => api.visits.list({ placeId, scope: 'mine', limit: 100 }),
    enabled: Boolean(placeId),
  });

  if (place.isLoading)
    return (
      <PageContainer>
        <Spinner label={t('app.loading')} />
      </PageContainer>
    );
  if (place.error || !place.data)
    return (
      <PageContainer className="max-w-3xl">
        <FeaturePageHeader title={t('tasting.places.title')} backTo="/tasting/places" />
        <Alert variant="destructive">{describeError(place.error)}</Alert>
      </PageContainer>
    );
  const p = place.data;

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader title={p.name} description={placeSubtitle(p)} backTo="/tasting/places">
        {p.website && (
          <a
            href={p.website}
            target="_blank"
            rel="noreferrer noopener"
            className="mt-1 inline-flex items-center gap-1 text-sm text-primary underline"
          >
            {t('tasting.visit.website')} <ExternalLink className="size-3" aria-hidden />
          </a>
        )}
      </FeaturePageHeader>
      <div className="space-y-4">
        <LazyTastingMap
          ariaLabel={t('tasting.visit.mapLabel', { place: p.name })}
          points={[{ id: p.id, lat: p.lat, lng: p.lng, label: p.name }]}
          center={{ lat: p.lat, lng: p.lng }}
          zoom={16}
          interactive={false}
          className="h-52 w-full overflow-hidden rounded-lg border"
        />
        <section aria-labelledby="place-visits" className="space-y-3">
          <h2 id="place-visits" className="text-lg font-semibold">
            {t('tasting.places.visitsHeading', { count: visits.data?.items.length ?? 0 })}
          </h2>
          {visits.isLoading ? (
            <Spinner label={t('app.loading')} />
          ) : (
            <ul className="grid gap-3 md:grid-cols-2">
              {(visits.data?.items ?? []).map((visit) => (
                <li key={visit.id}>
                  <VisitCard visit={visit} />
                </li>
              ))}
            </ul>
          )}
        </section>
        <PlaceEditor
          key={`${p.id}-${p.version}`}
          place={p}
          others={(all.data?.items ?? []).filter((o) => o.id !== p.id)}
        />
      </div>
    </PageContainer>
  );
}
